import React, { useState, useEffect } from "react";
import ChecklistCard from "../../ChecklistCard.js";
import "../../../styles/ResidentialListing.css";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import FormControl from "@mui/material/FormControl";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import importantNotes from "../../../data/Alberta/importantNotes.js";
import ImportantNotes from "../../ImportantNotes.js";
import BackToTop from "../../BackToTop.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

export default function ABAgriListing() {
  const [ListingData, setListingData] = useState([
    "Consumer Relationships Guide",
    "Agricultural Exclusive Seller Representation Agreement",
    "Land Title",
    "Schematic Drawings (RMS required - see notes)",
    "Related FINTRAC Documents",
    "MLS Sheet (Full Agent Detail View)",
  ]);

  const [isMLS, setisMLS] = useState("MLS");
  const [IsDisclosure, setIsDisclosure] = useState("No");
  const [IsDower, setIsDower] = useState("No");

  // State for conditional documents that can be added/removed
  const [conditionalDocuments, setConditionalDocuments] = useState({
    "Do you or close relatives own the property?": false,
    "Does the property have known latent defects?": false,
    "Dower Consent Required?": false,
  });

  // Function to get the corresponding listing data string based on document name and listing type
  const getDocumentName = (documentName) => {
    switch (documentName) {
      case "Do you or close relatives own the property?":
        return "Disclosure Requirements (see notes)";
      case "Does the property have known latent defects?":
        return isMLS === "Exclusive"
          ? "ESRA - Defect Disclosure Instruction Schedule"
          : "ESRA - Defect Disclosure Instruction Schedule (upload to MLS)";
      case "Dower Consent Required?":
        return "ESRA Dower Consent and Acknowledgement Form (see notes)";
      default:
        return "";
    }
  };

  const theme = createTheme({
    palette: {
      primary: {
        main: "#506CAA",
        dark: "#506CAA",
      },
      text: {
        primary: "#31303F",
        secondary: "#506CAA",
      },
    },
  });

  const setExclusive = (e) => {
    setisMLS("Exclusive");
    let newListingData = ListingData;
    if (
      newListingData.includes(
        "CREA - Duty of Cooperation Consent/Disclosure Form (see notes)"
      ) === true
    ) {
      return;
    } else {
      newListingData.push(
        "CREA - Duty of Cooperation Consent/Disclosure Form (see notes)"
      );
      newListingData.push(
        "Mandatory term in agreement: Seller does not want this property listed or marketed on the MLS system"
      );
    }
    setListingData(
      newListingData.filter(
        (item) =>
          item !== "MLS Sheet (Full Agent Detail View)" &&
          item !== "Schematic Drawings (RMS required - see notes)"
      )
    );
  };

  const setMLS = (e) => {
    setisMLS("MLS");
    let newListingData = ListingData;
    if (
      newListingData.includes("MLS Sheet (Full Agent Detail View)") === true ||
      newListingData.includes(
        "Schematic Drawings (RMS required - see notes)" === true
      )
    ) {
      return;
    } else {
      newListingData.push("MLS Sheet (Full Agent Detail View)");
      newListingData.push("Schematic Drawings (RMS required - see notes)");
    }
    setListingData(
      newListingData.filter(
        (item) =>
          item !==
            "CREA - Duty of Cooperation Consent/Disclosure Form (see notes)" &&
          item !==
            "Mandatory term in agreement: Seller does not want this property listed or marketed on the MLS system"
      )
    );
  };

  // Function to handle conditional document checkboxes
  const toggleConditionalDocument = (documentName) => {
    setConditionalDocuments((prev) => {
      const newState = {
        ...prev,
        [documentName]: !prev[documentName],
      };

      // Get the corresponding string for the listing data
      const listingDataString = getDocumentName(documentName);

      // Update IsDisclosure state if this is the property ownership checkbox
      if (documentName === "Do you or close relatives own the property?") {
        setIsDisclosure(newState[documentName] ? "Yes" : "No");
      }

      // Update IsDower state if this is the dower consent checkbox
      if (documentName === "Dower Consent Required?") {
        setIsDower(newState[documentName] ? "Yes" : "No");
      }

      // Update the listing data based on checkbox state
      setListingData((prevListingData) => {
        let newListingData = [...prevListingData];

        if (newState[documentName]) {
          // Add document if checked and not already present
          if (!newListingData.includes(listingDataString)) {
            newListingData.push(listingDataString);
          }
        } else {
          // Remove document if unchecked
          newListingData = newListingData.filter(
            (item) => item !== listingDataString
          );
        }

        return newListingData;
      });

      return newState;
    });
  };

  const checkSelections = () => {
    let baseListingData = [...ListingData];

    // Update latent defects document name based on listing type
    const oldLatentDefectsNameWithMLS =
      "ESRA - Defect Disclosure Instruction Schedule (upload to MLS)";
    const oldLatentDefectsNameWithoutMLS =
      "ESRA - Defect Disclosure Instruction Schedule";
    const newLatentDefectsName = getDocumentName(
      "Does the property have known latent defects?"
    );

    // Handle both directions: MLS to Exclusive and Exclusive to MLS
    if (baseListingData.includes(oldLatentDefectsNameWithMLS)) {
      baseListingData = baseListingData.map((item) =>
        item === oldLatentDefectsNameWithMLS ? newLatentDefectsName : item
      );
    }
    if (baseListingData.includes(oldLatentDefectsNameWithoutMLS)) {
      baseListingData = baseListingData.map((item) =>
        item === oldLatentDefectsNameWithoutMLS ? newLatentDefectsName : item
      );
    }

    // Add MLS-specific documents
    if (isMLS === "MLS") {
      if (
        !baseListingData.includes(
          "Schematic Drawings (RMS required - see notes)"
        )
      ) {
        baseListingData.push("Schematic Drawings (RMS required - see notes)");
      }
      if (!baseListingData.includes("MLS Sheet (Full Agent Detail View)")) {
        baseListingData.push("MLS Sheet (Full Agent Detail View)");
      }
    } else if (isMLS === "Exclusive") {
      if (
        !baseListingData.includes(
          "CREA - Duty of Cooperation Consent/Disclosure Form (see notes)"
        )
      ) {
        baseListingData.push(
          "CREA - Duty of Cooperation Consent/Disclosure Form (see notes)"
        );
      }
      if (
        !baseListingData.includes(
          "Mandatory term in agreement: Seller does not want this property listed or marketed on the MLS system"
        )
      ) {
        baseListingData.push(
          "Mandatory term in agreement: Seller does not want this property listed or marketed on the MLS system"
        );
      }
    }

    setListingData(baseListingData);
  };

  useEffect(() => {
    checkSelections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMLS, IsDower]);

  return (
    <div className="checklists w-full flex flex-col items-center justify-center">
      <div className="flex flex-col w-full pl-4 ">
        <div className="flex flex-col items-left pb-4">
          <div className="clause-card-content">
            <div className="clause-warning">
              <div className="clause-warning-icon">
                <FontAwesomeIcon icon={faTriangleExclamation} size="lg" />
              </div>
              <div>
                Trading Agricultural Real Estate in Alberta requires a Rural
                License. If you do not have this license, you cannot trade
                agricultural real estate and must refer the client to an agent
                with the appropriate license.
              </div>
            </div>
          </div>

          <div className="pr-5 ">Type of Listing?</div>
          <ThemeProvider theme={theme}>
            <FormControl>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                defaultValue="MLS"
              >
                <FormControlLabel
                  value="MLS"
                  control={<Radio size="small" onClick={setMLS} />}
                  label="MLS"
                />
                <FormControlLabel
                  value="Exclusive"
                  control={<Radio size="small" onClick={setExclusive} />}
                  label="Exclusive"
                />
              </RadioGroup>
            </FormControl>
          </ThemeProvider>
        </div>

        {/* Check all that apply section */}
        <div className="flex flex-col items-left pb-4">
          <div className="pr-5 pb-2 ">
            For each "Yes" response, check the box that applies:
          </div>
          <ThemeProvider theme={theme}>
            <div className="flex flex-col ">
              {Object.keys(conditionalDocuments).map((documentName) => (
                <FormControlLabel
                  key={documentName}
                  control={
                    <Checkbox
                      size="small"
                      checked={conditionalDocuments[documentName]}
                      onChange={() => toggleConditionalDocument(documentName)}
                    />
                  }
                  label={documentName}
                />
              ))}
            </div>
          </ThemeProvider>
        </div>
      </div>

      {IsDisclosure === "Yes" ? (
        <>
          <div className="flex w-full pl-4 pt-3 items-center ">
            <div className="pb-5 pr-3">
              <div className="float-left mr-3 pt-2">
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  size="2xl"
                  color="#cc0000"
                />
              </div>
              <div>
                You cannot represent both clients in a trade when you have
                interest in the listed property. As a result, if you bring a
                Buyer client to the trade, you must release or refer one of the
                client(s) to another agent. Additionally, you can collect a
                referral fee on the client referral.
              </div>
            </div>
          </div>
        </>
      ) : (
        ""
      )}
      <ChecklistCard
        province={"AB"}
        Title1={""}
        CheckListData={ListingData}
        Header={"Agricultural Listing:"}
        Header2={"Seller Client"}
      />
      <div className="important-notes flex flex-col w-full justify-left pt-6">
        <strong>Important Notes:</strong>
        <div className="pb-3"></div>

        {IsDisclosure === "Yes" ? (
          <ImportantNotes
            Notes={importantNotes.filter(
              (notes) => notes.category.includes("disclosure") === true
            )}
          />
        ) : (
          ""
        )}
        {IsDower === "Yes" ? (
          <ImportantNotes
            Notes={importantNotes.filter(
              (notes) => notes.category.includes("dowerListing") === true
            )}
          />
        ) : (
          ""
        )}
        {isMLS === "MLS" ? (
          <ImportantNotes
            Notes={importantNotes.filter(
              (notes) => notes.category.includes("mlsMeasure") === true
            )}
          />
        ) : (
          ""
        )}
        {isMLS === "Exclusive" ? (
          <ImportantNotes
            Notes={importantNotes.filter(
              (notes) => notes.category.includes("exclusiveListing") === true
            )}
          />
        ) : (
          ""
        )}
        <ImportantNotes
          Notes={importantNotes.filter(
            (notes) => notes.category.includes("listing") === true
          )}
        />
        {isMLS === "MLS" ? (
          <ImportantNotes
            Notes={importantNotes.filter(
              (notes) => notes.category.includes("mls") === true
            )}
          />
        ) : (
          ""
        )}
      </div>
      <BackToTop />
    </div>
  );
}
